import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Phone,
  MessageSquare,
  Tag,
  FileText,
  CheckSquare,
  Calendar,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColors = (name) => {
  // Generate consistent colors based on name
  const colors = [
    { bg: 'bg-blue-500', text: 'text-white' },
    { bg: 'bg-emerald-500', text: 'text-white' },
    { bg: 'bg-purple-500', text: 'text-white' },
    { bg: 'bg-amber-500', text: 'text-white' },
    { bg: 'bg-pink-500', text: 'text-white' },
    { bg: 'bg-cyan-500', text: 'text-white' },
    { bg: 'bg-indigo-500', text: 'text-white' },
    { bg: 'bg-rose-500', text: 'text-white' },
  ];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const QuickActionButton = ({ icon: Icon, tooltip, onClick, badge, active }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className={cn(
            'relative p-1 text-slate-400 hover:text-slate-600 rounded transition-colors',
            active && 'text-emerald-600'
          )}
        >
          <Icon className="h-4 w-4" />
          {badge && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
              {badge}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const OpportunityCard = ({ opportunity, isDragging = false, overlay = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: opportunity.id,
    data: {
      type: 'opportunity',
      opportunity,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isBeingDragged = isDragging || isSortableDragging;

  // Get contact name - use title as fallback
  const contactName = opportunity.contact_name || opportunity.title || 'Unknown Contact';
  const avatarColors = getAvatarColors(contactName);
  const sourceLabel = opportunity.source_detail || opportunity.source || 'Unknown';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'bg-white rounded-lg border border-slate-200 p-3 cursor-grab active:cursor-grabbing',
        'hover:shadow-md transition-all duration-200',
        'select-none touch-none',
        isBeingDragged && 'opacity-50 shadow-lg ring-2 ring-blue-500',
        overlay && 'shadow-xl rotate-1'
      )}
      data-testid={`opportunity-card-${opportunity.id}`}
    >
      {/* Row 1: Name + Avatar */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-semibold text-stone-900 text-sm leading-tight">
          {contactName}
        </h4>
        <div className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0',
          avatarColors.bg,
          avatarColors.text
        )}>
          {getInitials(contactName)}
        </div>
      </div>

      {/* Row 2: Opportunity Source */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs text-slate-500">Opportunity So...</span>
        <span className="text-xs font-medium text-emerald-600">{sourceLabel}</span>
      </div>

      {/* Row 3: Opportunity Value */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-slate-500">Opportunity Val...</span>
        <span className="text-xs font-semibold text-stone-900">{formatCurrency(opportunity.value)}</span>
      </div>

      {/* Quick Actions Row */}
      <div className="flex items-center gap-0.5 pt-2 border-t border-slate-100">
        <QuickActionButton icon={Phone} tooltip="Call" />
        <QuickActionButton icon={MessageSquare} tooltip="View conversations" />
        <QuickActionButton 
          icon={Tag} 
          tooltip="Tags" 
          badge={opportunity.tags?.length || null}
          active={opportunity.tags?.length > 0}
        />
        <QuickActionButton icon={FileText} tooltip="Add Note" />
        <QuickActionButton icon={CheckSquare} tooltip="Add Task" />
        <QuickActionButton icon={Calendar} tooltip="Add an appointment" />
      </div>
    </div>
  );
};

export default OpportunityCard;
